import React, { useState, useEffect, useRef } from 'react';
import Icon, { IconType } from '../Icon/Icon';
import imgClouds from '../../../public/Assets/Images/clouds.jpg';

export interface IExpandableImgProps {
  imageSrc: IImage[];
  name?: string;
  onClick?: Function;
}

export interface IImage {
  path: string;
  alt: string;
  missing?: boolean;
  type?: 'person' | 'other';
}

export const ExpandableImg = (props: IExpandableImgProps) => {
  const [images, setImages] = useState<IImage[]>(props.imageSrc ?? []);
  const [index, setIndex] = useState<number>(0);
  const [open, setOpen] = useState<boolean>(false);
  const [showFull, setShowFull] = useState<boolean>(false);
  const [showBig, setShowBig] = useState<boolean>(false);

  return (
    <div
      className={`expandable-img ${open ? 'open' : ''}`}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (open) {
          setOpen(false);
        }
      }}>
      {open && (
        <div
          className="close-btn"
          onClick={() => {
            setOpen(false);
          }}>
          <Icon type={IconType.close} />
        </div>
      )}
      {!(images[index]?.missing ?? false) ? (
        <img
          alt={images[index]?.alt ?? ''}
          src={images[index]?.path}
          onError={({ currentTarget: x }) => {
            const i = index + 1;
            if (i - 1 >= images.length || !images[i]) {
              x.onerror = null; // prevents looping
              x.src = imgClouds;
            } else {
              x.src = images[i].path;
              x.alt = images[i].alt;
            }
            setIndex(i);
          }}
          onClick={(e) => {
            if (!open) {
              if (props?.onClick) {
                props.onClick();
                return;
              }
              setOpen(true);
            } else {
              console.log('stop');
              e.stopPropagation();
            }
          }}
        />
      ) : (
        <>
          <div className="missing-image">
            <Icon type={images[index]?.type === 'person' ? IconType.userLight : IconType.image} />
          </div>
        </>
      )}
      {images[index]?.alt && <div className="caption">{images[index].alt}</div>}
      {props?.name && open && <div>{props.name}</div>}
    </div>
  );
};

export interface IExpandableVideoProps {
  videoSrc: IVideo[];
  name?: string;
  onClick?: Function;
}

export interface IVideo {
  path: string;
  alt: string;
}

export const ExpandableVideo = (props: IExpandableVideoProps) => {
  const [videos, setVideos] = useState<IVideo[]>(props.videoSrc ?? []);
  const [index, setIndex] = useState<number>(0);
  const [open, setOpen] = useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (open && videoRef.current) {
      videoRef.current.play();
    }
  }, [open]);

  return (
    <div
      className={`expandable-img ${open ? 'open' : ''}`}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (open) {
          setOpen(false);
          if (videoRef.current) {
            videoRef.current.pause();
          }
        }
      }}>
      {open && (
        <div
          className="close-btn"
          onClick={() => {
            setOpen(false);
            if (videoRef.current) {
              videoRef.current.pause();
            }
          }}>
          <Icon type={IconType.close} />
        </div>
      )}
      {!open ? (
        <div
          className="video-placeholder"
          onClick={() => {
            if (props?.onClick) {
              props.onClick();
              return;
            }
            setOpen(true);
          }}>
          <div className="video-label">
            <Icon type={IconType.film} /> Video
          </div>
          {videos[index]?.alt && <div className="caption">{videos[index].alt}</div>}
        </div>
      ) : (
        <video
          ref={videoRef}
          controls
          src={videos[index]?.path}
          onClick={(e) => {
            e.stopPropagation();
          }}
        />
      )}
      {props?.name && open && <div>{props.name}</div>}
    </div>
  );
};

interface IImageDetails {
  comments?: string;
  path: string;
}

interface ISlickImage {
  image: IImageDetails;
  noDefaultImage?: boolean;
}

export const SlickImage = (props: ISlickImage) => {
  const getImage = () => {
    let image = props.image;

    if (image) {
      return (
        <div className="tile-image">
          <img
            alt={image.comments}
            src={image.path}
            className="body-grow"
            onError={({ currentTarget }) => {
              currentTarget.onerror = null; // prevents looping
              currentTarget.style.display = 'none';
            }}
          />
          <img
            alt={image.comments}
            src={image.path}
            className="body-grow img-back"
            onError={({ currentTarget }) => {
              currentTarget.onerror = null; // prevents looping
              if (props.noDefaultImage) {
                currentTarget.style.display = 'none';
              } else {
                currentTarget.src = imgClouds;
              }
            }}
          />
        </div>
      );
    }

    return (
      <div className="tile-image">
        <img src={imgClouds} className="stock-image body-grow" />
      </div>
    );
  };

  return <>{getImage()}</>;
};

interface ISlickImageGroup {
  images: IImageDetails[];
  onErrorImage?: string;
  errorShow?: React.ReactNode;
  className?: string;
  imageClassName?: string;
}
export const SlickImageGroup = (props: ISlickImageGroup) => {
  const [gridSize, setGridSize] = useState<number>(1);

  useEffect(() => {
    setGridSize(getGridSize());
  }, []);

  const getGridSize = () => {
    const size = props.images.length;
    if (size > 9) return 5;
    if (size > 6) return 4;
    if (size > 4) return 3;
    // if(size > 2) return 2;
    if (size > 1) return 2;
    return 0;
  };
  return (
    <>
      <div className={`slick-image-group no-${gridSize} ${props.className ?? ''}`}>
        {props.images.map((x, i) => {
          return (
            <img
              className={`${props.imageClassName ?? ''}`}
              src={x.path}
              title={x.comments}
              key={`img-gr-${i}`}
              onError={({ currentTarget }) => {
                currentTarget.onerror = null; // prevents looping
                if (props.onErrorImage) {
                  currentTarget.src = props.onErrorImage;
                } else {
                  currentTarget.style.display = 'none';
                }
              }}
            />
          );
        })}
      </div>
    </>
  );
};

interface IEasyImageProps {
  alt?: string;
  src: string;
  link?: string;
  title?: string;
  missingImagePath?: string;
  showCloudsOnError?: boolean;
  className?: string;
}

export const EasyImage = (props: IEasyImageProps) => {
  const [src, setSrc] = useState<string>();
  const [hasError, setHasError] = useState<boolean>(false);

  useEffect(() => {
    setSrc(props.src);
  }, [props.src]);

  const buildImg = () => {
    if (props.link) {
      return (
        <a title={props.title} href={props.link}>
          <img
            alt={props.alt}
            src={props.src}
            className={`easy-image ${props.className ?? ''}`}
            onError={(x) => {
              const currentTarget = x.currentTarget;
              if (props.missingImagePath && props.missingImagePath !== currentTarget.src) {
                currentTarget.src = props.missingImagePath;
              } else if (props.showCloudsOnError) {
                currentTarget.src = imgClouds;
              } else {
                setHasError(true);
                //currentTarget.style.display = "none";
              }
            }}
          />
        </a>
      );
    }
    return (
      <img
        alt={props.alt}
        src={props.src}
        className={`easy-image ${props.className ?? ''}`}
        onError={(x) => {
          const currentTarget = x.currentTarget;
          if (props.missingImagePath && props.missingImagePath !== currentTarget.src) {
            currentTarget.src = props.missingImagePath;
          } else if (props.showCloudsOnError) {
            currentTarget.src = imgClouds;
          } else {
            setHasError(true);
            //currentTarget.style.display = "none";
          }
        }}
      />
    );
  };

  return <>{src && <>{!hasError && buildImg()}</>}</>;
};
